import React, { useState } from 'react'
import classNames from 'classnames'
import { Trash2 } from 'lucide-react'
import { isMobile } from 'react-device-detect'
import { AvatarType, SampleFile } from 'service/aiStudio'
import { UserFile } from 'service/user'
import { ImageSelectionModal } from 'pages/ai-studio/components/ImageSelectionModal'
import { ExampleList } from '../ExampleList'
import { SampleSelectionModal } from 'pages/ai-studio/components/SampleSelectionModal'
import { ClothingData } from 'pages/ai-studio/tabs/FashionEditorTab/type/type'

interface StyleConfirmStepProps {
  selectedAvatar: AvatarType | null
  clothingData: ClothingData
  setClothingData: (data: ClothingData) => void
}

const categories = [
  { id: 'top', label: '상의', selected: true },
  { id: 'bottom', label: '하의', selected: false },
  { id: 'dress', label: '원피스', selected: false },
] as const

export const StyleConfirmStep: React.FC<StyleConfirmStepProps> = ({
  selectedAvatar,
  clothingData,
  setClothingData,
}) => {
  const [isTopModalOpen, setIsTopModalOpen] = useState(false)
  const [isBottomModalOpen, setIsBottomModalOpen] = useState(false)
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false)
  const [garmentType, setGarmentType] = useState(
    clothingData.category === 'bottom' ? 'top' : 'bottom',
  )


  // Helper function to determine slot assignments based on category
  const getSlotConfig = () => {
    switch (clothingData.category) {
      case 'top':
        return {
          firstSlot: { type: 'top', label: '상의', isOptional: false },
          secondSlot: {
            type: 'bottom',
            label: '함께 코디할 하의',
            isOptional: true,
          },
        }
      case 'bottom':
        return {
          firstSlot: { type: 'bottom', label: '하의', isOptional: false },
          secondSlot: {
            type: 'top',
            label: '함께 코디할 상의',
            isOptional: true,
          },
        }
      case 'dress':
        return {
          firstSlot: { type: 'dress', label: '원피스', isOptional: false },
          secondSlot: null,
        }
      default:
        return {
          firstSlot: { type: 'top', label: '상의', isOptional: false },
          secondSlot: {
            type: 'bottom',
            label: '함께 코디할 하의',
            isOptional: true,
          },
        }
    }
  }

  const handleCategorySelect = (category: 'top' | 'bottom' | 'dress') => {
    const paramGarmentType = category === 'top' ? 'bottom' : 'top'
    setGarmentType(paramGarmentType)
    setClothingData({
      ...clothingData,
      category,
      // 카테고리 변경 시 모든 이미지 데이터 초기화
      topImage: null,
      bottomImage: null,
      topImageId: null,
      bottomImageId: null,
      bottomParsedGarmentInfo: null,
      topParsedGarmentInfo: null,
      topDescription: '',
      bottomDescription: '',
      combinedImage: [],
      generatedImages: [],
      isUseSample: false,
    })
  }

  const handleFirstSlotImageSelect = (file: UserFile) => {
    const slotConfig = getSlotConfig()
    const updateData: Partial<ClothingData> = {
      combinedImage: [],
      generatedImages: [],
    }

    if (
      slotConfig.firstSlot.type === 'top' ||
      slotConfig.firstSlot.type === 'dress'
    ) {
      updateData.topImage = file.fileUrl
      updateData.topImageId = file.id
      updateData.topDescription = file.garmentInfo?.description ?? ''
      updateData.topParsedGarmentInfo = file.editedGarmentInfo || file.parsedGarmentInfo
    } else if (slotConfig.firstSlot.type === 'bottom') {
      updateData.bottomImage = file.fileUrl
      updateData.bottomImageId = file.id
      updateData.bottomDescription = file.garmentInfo?.description ?? ''
      updateData.bottomParsedGarmentInfo = file.editedGarmentInfo || file.parsedGarmentInfo
    }

    setClothingData({
      ...clothingData,
      ...updateData,
    })
  }

  const handleSecondSlotImageSelect = (file: UserFile) => {
    const slotConfig = getSlotConfig()

    const updateData: Partial<ClothingData> = {
      combinedImage: [],
      generatedImages: [],
    }

    if (slotConfig.secondSlot?.type === 'top') {
      updateData.topImage = file.fileUrl
      updateData.topImageId = file.id
      updateData.topDescription = file.garmentInfo?.description ?? ''
      updateData.topParsedGarmentInfo = file.editedGarmentInfo || file.parsedGarmentInfo
    } else if (slotConfig.secondSlot?.type === 'bottom') {
      updateData.bottomImage = file.fileUrl
      updateData.bottomImageId = file.id
      updateData.bottomDescription = file.garmentInfo?.description ?? ''
      updateData.bottomParsedGarmentInfo = file.editedGarmentInfo || file.parsedGarmentInfo
    }

    setClothingData({
      ...clothingData,
      ...updateData,
    })
  }

  const handleDeleteFirstSlotImage = () => {
    const slotConfig = getSlotConfig()
    const updateData: Partial<ClothingData> = {
      combinedImage: [],
      generatedImages: [],
    }

    if (
      slotConfig.firstSlot.type === 'top' ||
      slotConfig.firstSlot.type === 'dress'
    ) {
      updateData.topImage = null
      updateData.topImageId = null
      updateData.topDescription = ''
      updateData.topParsedGarmentInfo = null
    } else if (slotConfig.firstSlot.type === 'bottom') {
      updateData.bottomImage = null
      updateData.bottomImageId = null
      updateData.bottomDescription = ''
      updateData.bottomParsedGarmentInfo = null
    }

    setClothingData({
      ...clothingData,
      ...updateData,
    })
  }

  const handleDeleteSecondSlotImage = () => {
    const slotConfig = getSlotConfig()
    const updateData: Partial<ClothingData> = {
      combinedImage: [],
      generatedImages: [],
      isUseSample: false,
      samples: [],
    }

    if (slotConfig.secondSlot?.type === 'top') {
      updateData.topImage = null
      updateData.topImageId = null
      updateData.topDescription = ''
      updateData.topParsedGarmentInfo = null
    } else if (slotConfig.secondSlot?.type === 'bottom') {
      updateData.bottomImage = null
      updateData.bottomImageId = null
      updateData.bottomDescription = ''
      updateData.bottomParsedGarmentInfo = null
    }

    setClothingData({
      ...clothingData,
      ...updateData,
    })
  }

  const handleSampleSelect = (samples: SampleFile[]) => {
    if (garmentType === 'top') {
      // 상의 카테고리 → 하의 슬롯에 샘플 적용
      setClothingData({
        ...clothingData,
        topImage: samples.map((s) => s.imageUrl),
        isUseSample: true,
        samples: samples,
      })
    } else {
      // 하의 카테고리 → 상의 슬롯에 샘플 적용
      setClothingData({
        ...clothingData,
        bottomImage: samples.map((s) => s.imageUrl),
        isUseSample: true,
        samples: samples,
      })
    }
  }

  if (isMobile) {
    // 모바일 전용 레이아웃
    return (
      <div className="flex h-full flex-col gap-4 overflow-y-auto">
        {/* 제목 섹션 */}
        <div className="flex flex-col gap-1 md:gap-2">
          <h2 className="text-[18px] font-bold text-[#2E343D]">
            옷 이미지 선택하기
          </h2>
          <p className="text-base text-[#2E343D]">
            모델에게 입힐 옷의 이미지를 선택해주세요!
          </p>
        </div>

        {/* 메인 컨텐츠 영역 */}
        <div className="flex flex-col gap-4 rounded-[10px] bg-[#F6F8FB] p-4">
          {/* 카테고리 선택 - 모바일 최적화 */}
          <div className="flex justify-center gap-4">
            {categories.map((category) => {
              const isSelected = clothingData.category === category.id

              return (
                <div
                  key={category.id}
                  className="flex cursor-pointer items-center gap-2"
                  onClick={() => handleCategorySelect(category.id)}
                >
                  <div className="relative h-5 w-5">
                    <div
                      className={classNames(
                        'h-5 w-5 rounded-full border bg-white',
                        {
                          'border-[#00B7B9]': isSelected,
                          'border-[#AEAEB2]': !isSelected,
                        },
                      )}
                    />
                    {isSelected && (
                      <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-[#00B7B9]" />
                    )}
                  </div>
                  <span className="text-lg text-[#2E343D]">
                    {category.label}
                  </span>
                </div>
              )
            })}
          </div>

          {/* 이미지 선택 영역 - 모바일 최적화 */}
          <div className="flex justify-center gap-4">
            {(() => {
              const slotConfig = getSlotConfig()

              return (
                <>
                  {/* 첫 번째 슬롯 */}
                  <div className="relative">
                    <div
                      className="flex h-[242px] w-[136px] cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-[#D9DEE8] bg-white"
                      onClick={() => setIsTopModalOpen(true)}
                    >
                      {(() => {
                        const image =
                          slotConfig.firstSlot.type === 'top' ||
                          slotConfig.firstSlot.type === 'dress'
                            ? clothingData.topImage
                            : clothingData.bottomImage
                        const emoji =
                          slotConfig.firstSlot.type === 'dress'
                            ? '👗'
                            : slotConfig.firstSlot.type === 'top'
                              ? '👚'
                              : '👖'
                        const altText = `${slotConfig.firstSlot.label} 이미지`

                        return image ? (
                          <div className="flex h-full w-full flex-wrap justify-between">
                            {typeof image === 'object' ? (
                              <img
                                src={image[0]}
                                alt={altText}
                                className="h-full w-full rounded-[10px] object-cover"
                              />
                            ) : (
                              <img
                                src={image}
                                alt={altText}
                                className="h-full w-full rounded-[10px] object-cover"
                              />
                            )}
                            {/* 투명 오버레이 */}
                          </div>
                        ) : (
                          <>
                            <div className="text-4xl">{emoji}</div>
                            <div className="text-center">
                              <p className="text-sm font-medium text-[#2E343D]">
                                {slotConfig.firstSlot.label}
                              </p>
                              <p className="mt-1 rounded-lg border-[1px] p-1 text-xs text-[#8F929B]">
                                이미지 선택
                              </p>
                            </div>
                          </>
                        )
                      })()}
                    </div>

                    {/* 삭제 버튼 */}
                    {(() => {
                      const image =
                        slotConfig.firstSlot.type === 'top' ||
                        slotConfig.firstSlot.type === 'dress'
                          ? clothingData.topImage
                          : clothingData.bottomImage

                      return (
                        image && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteFirstSlotImage()
                            }}
                            className="absolute bottom-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black bg-opacity-70 transition-opacity hover:bg-opacity-90"
                          >
                            <Trash2 size={16} className="text-white" />
                          </button>
                        )
                      )
                    })()}
                  </div>

                  {/* 두 번째 슬롯 (원피스가 아닌 경우에만) */}
                  {slotConfig.secondSlot && (
                    <div className="relative">
                      <div
                        className="flex h-[242px] w-[136px] cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-[#D9DEE8] bg-white"
                        onClick={() => {
                          setIsSampleModalOpen(true)
                        }}
                      >
                        {(() => {
                          const image =
                            slotConfig.secondSlot.type === 'top'
                              ? clothingData.topImage
                              : clothingData.bottomImage
                          const emoji =
                            slotConfig.secondSlot.type === 'top' ? '👚' : '👖'
                          const altText = `${slotConfig.secondSlot.label} 이미지`

                          return image ? (
                            <>
                              {typeof image === 'object' ? (
                                <div className="flex h-full w-full flex-wrap justify-between">
                                  {clothingData.samples.map((el, index) => (
                                    <img
                                      key={index}
                                      src={el.thumbnailUrl}
                                      alt={altText}
                                      className="h-full w-full object-cover"
                                    />
                                  ))}
                                </div>
                              ) : (
                                <img
                                  src={image}
                                  alt={altText}
                                  className="h-full w-full rounded-[10px] object-cover"
                                />
                              )}
                            </>
                          ) : (
                            <>
                              <div className="text-4xl">{emoji}</div>
                              <div className="text-center">
                                <p className="text-sm font-medium text-[#2E343D]">
                                  {slotConfig.secondSlot.label}
                                </p>
                                <p className="mt-1 text-xs text-[#8F929B]">
                                  추천 매칭 선택
                                </p>
                              </div>
                            </>
                          )
                        })()}
                      </div>
                      {/* 삭제 버튼 */}
                      {(() => {
                        const image =
                          slotConfig.secondSlot.type === 'top'
                            ? clothingData.topImage
                            : clothingData.bottomImage

                        return (
                          image && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteSecondSlotImage()
                              }}
                              className="absolute bottom-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black bg-opacity-70 transition-opacity hover:bg-opacity-90"
                            >
                              <Trash2 size={16} className="text-white" />
                            </button>
                          )
                        )
                      })()}
                      {/* 선택사항 태그 */}
                      {(() => {
                        const image =
                          slotConfig.secondSlot.type === 'top'
                            ? clothingData.topImage
                            : clothingData.bottomImage

                        return (
                          !image &&
                          slotConfig.secondSlot.isOptional && (
                            <div
                              className="absolute bottom-0 left-0 flex w-full cursor-pointer justify-center rounded-[4px] bg-[#8F929B] px-2 py-2"
                              onClick={() => {
                                setIsBottomModalOpen((prev) => !prev)
                              }}
                            >
                              <span className="text-xs text-white">
                                내 이미지 선택
                              </span>
                            </div>
                          )
                        )
                      })()}
                    </div>
                  )}
                </>
              )
            })()}
          </div>

          {/* 이미지 품질 가이드 */}
          {/* <div className="w-full">
            <p className="mb-2 text-center text-sm font-bold text-[#FD6767]">
              깨끗한 배경에 전체가 잘 보이는 이미지를 사용해주세요!
            </p>
            <ExampleList width="w-[58px]" height="h-[58px]" />
          </div> */}
        </div>

        {/* 이미지 선택 모달들 */}
        <ImageSelectionModal
          isOpen={isTopModalOpen}
          onClose={() => setIsTopModalOpen(false)}
          onImageSelect={handleFirstSlotImageSelect}
        />

        <ImageSelectionModal
          isOpen={isBottomModalOpen}
          onClose={() => setIsBottomModalOpen(false)}
          onImageSelect={handleSecondSlotImageSelect}
        />
        <SampleSelectionModal
          isOpen={isSampleModalOpen}
          onClose={() => setIsSampleModalOpen(false)}
          garmentType={garmentType}
          onSample={handleSampleSelect}
        />
      </div>
    )
  }

  // 데스크톱 레이아웃 (기존)
  return (
    <div className="flex h-full flex-col gap-4">
      {/* 제목 섹션 */}
      <div className="flex flex-col gap-2">
        <h2 className="text-[18px] font-bold text-[#2E343D]">
          옷 이미지 선택하기
        </h2>
        <p className="text-base text-[#2E343D]">
          모델에게 입힐 옷의 이미지를 선택해주세요!
        </p>
      </div>

      {/* 메인 컨텐츠 영역 */}
      <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-[10px] bg-[#F6F8FB] p-4">
        {/* 카테고리 선택 */}
        <div className="flex justify-center gap-4">
          {categories.map((category) => {
            const isSelected = clothingData.category === category.id

            return (
              <div
                key={category.id}
                className="flex w-[100px] cursor-pointer items-center gap-2"
                onClick={() => handleCategorySelect(category.id)}
              >
                <div className="relative h-5 w-5">
                  <div
                    className={classNames(
                      'h-5 w-5 rounded-full border bg-white',
                      {
                        'border-[#00B7B9]': isSelected,
                        'border-[#AEAEB2]': !isSelected,
                      },
                    )}
                  />
                  {isSelected && (
                    <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-[#00B7B9]" />
                  )}
                </div>
                <span className="text-lg text-[#2E343D]">{category.label}</span>
              </div>
            )
          })}
        </div>

        {/* 이미지 선택 영역 */}
        <div className="flex justify-center gap-4">
          {(() => {
            const slotConfig = getSlotConfig()

            return (
              <>
                {/* 첫 번째 슬롯 */}
                <div className="relative">
                  <div
                    className="flex h-[242px] w-[136px] cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-[#D9DEE8] bg-white"
                    onClick={() => setIsTopModalOpen(true)}
                  >
                    {(() => {
                      const image =
                        slotConfig.firstSlot.type === 'top' ||
                        slotConfig.firstSlot.type === 'dress'
                          ? clothingData.topImage
                          : clothingData.bottomImage
                      const emoji =
                        slotConfig.firstSlot.type === 'dress'
                          ? '👗'
                          : slotConfig.firstSlot.type === 'top'
                            ? '👚'
                            : '👖'
                      const altText = `${slotConfig.firstSlot.label} 이미지`

                      return image ? (
                        <>
                          {typeof image === 'object' ? (
                            <div>
                              <img
                                src={image[0]}
                                alt={altText}
                                className="h-full w-full rounded-[10px] object-cover"
                              />
                            </div>
                          ) : (
                            <img
                              src={image}
                              alt={altText}
                              className="h-full w-full rounded-[10px] object-cover"
                            />
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-4xl">{emoji}</div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-[#2E343D]">
                              {slotConfig.firstSlot.label}
                            </p>
                            <p className="mt-1 rounded-lg border-[1px] p-1 text-xs text-[#8F929B]">
                              이미지 선택
                            </p>
                          </div>
                        </>
                      )
                    })()}
                  </div>

                  {/* 삭제 버튼 */}
                  {(() => {
                    const image =
                      slotConfig.firstSlot.type === 'top' ||
                      slotConfig.firstSlot.type === 'dress'
                        ? clothingData.topImage
                        : clothingData.bottomImage

                    return (
                      image && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteFirstSlotImage()
                          }}
                          className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black bg-opacity-70 transition-opacity hover:bg-opacity-90"
                        >
                          <Trash2 size={16} className="text-white" />
                        </button>
                      )
                    )
                  })()}
                </div>

                {/* 두 번째 슬롯 (원피스가 아닌 경우에만) */}
                {slotConfig.secondSlot && (
                  <div className="relative">
                    <div
                      className="flex h-[242px] w-[136px] cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border border-dashed border-[#D9DEE8] bg-white"
                      onClick={() => {
                        setIsSampleModalOpen(true)
                      }}
                    >
                      {(() => {
                        const image =
                          slotConfig.secondSlot.type === 'top'
                            ? clothingData.topImage
                            : clothingData.bottomImage
                        const emoji =
                          slotConfig.secondSlot.type === 'top' ? '👚' : '👖'
                        const altText = `${slotConfig.secondSlot.label} 이미지`

                        return image ? (
                          <>
                            {typeof image === 'object' ? (
                              <div className="flex h-full w-full flex-wrap justify-between">
                                {clothingData.samples.map((el, index) => (
                                  <img
                                    key={index}
                                    src={el.thumbnailUrl}
                                    alt={altText}
                                    className="h-full w-full object-cover"
                                  />
                                ))}
                              </div>
                            ) : (
                              <img
                                src={image}
                                alt={altText}
                                className="h-full w-full rounded-[10px] object-cover"
                              />
                            )}
                          </>
                        ) : (
                          <>
                            <div className="text-4xl">{emoji}</div>
                            <div className="text-center">
                              <p className="text-sm font-medium text-[#2E343D]">
                                {slotConfig.secondSlot.label}
                              </p>
                              <p className="mt-1 rounded-lg border-[1px] p-1 text-xs text-[#8F929B]">
                                추천 매칭 선택
                              </p>
                            </div>
                          </>
                        )
                      })()}
                    </div>
                    {/* 삭제 버튼 */}
                    {(() => {
                      const image =
                        slotConfig.secondSlot.type === 'top'
                          ? clothingData.topImage
                          : clothingData.bottomImage

                      return (
                        image && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteSecondSlotImage()
                            }}
                            className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black bg-opacity-70 transition-opacity hover:bg-opacity-90"
                          >
                            <Trash2 size={16} className="text-white" />
                          </button>
                        )
                      )
                    })()}
                    {/* 선택사항 태그 */}
                    {(() => {
                      const image =
                        slotConfig.secondSlot.type === 'top'
                          ? clothingData.topImage
                          : clothingData.bottomImage

                      return (
                        !image &&
                        slotConfig.secondSlot.isOptional && (
                          <div
                            className="absolute bottom-0 left-0 flex w-full cursor-pointer justify-center rounded-[4px] bg-[#8F929B] px-2 py-2"
                            onClick={() => {
                              setIsBottomModalOpen((prev) => !prev)
                            }}
                          >
                            <span className="text-xs text-white">
                              내 이미지 선택
                            </span>
                          </div>
                        )
                      )
                    })()}{' '}
                  </div>
                )}
              </>
            )
          })()}
        </div>

        {/* 이미지 선택 모달들 */}
        <ImageSelectionModal
          isOpen={isTopModalOpen}
          onClose={() => setIsTopModalOpen(false)}
          onImageSelect={handleFirstSlotImageSelect}
        />

        <ImageSelectionModal
          isOpen={isBottomModalOpen}
          onClose={() => setIsBottomModalOpen(false)}
          onImageSelect={handleSecondSlotImageSelect}
        />

        <SampleSelectionModal
          isOpen={isSampleModalOpen}
          onClose={() => setIsSampleModalOpen(false)}
          garmentType={garmentType}
          onSample={handleSampleSelect}
        />

        {/* 이미지 품질 가이드 */}
        {/* <div className="w-full">
          <p className="mb-1 text-center text-sm font-bold text-[#FD6767]">
            깨끗한 배경에 전체가 잘 보이는 이미지를 사용해주세요!
          </p>
          <ExampleList width="w-[68px]" height="h-[68px]" />
        </div> */}
      </div>
    </div>
  )
}
